import {
  TourModel,
  useUncloseTour,
} from 'api-hooks/tour/tour-list/tour-detail';
import { CloseSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import { TourStatusEnum } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import DeleteButton from 'components/common/delete-button';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useUncloseTourAction(tour: TourModel) {
  const { t } = useTranslation();
  const uncloseMutate = useUncloseTour();
  const dialog = useDialog();
  const onDismiss = useDismiss('tours');
  const { onRequestApproval } = useApprovalAction('tours');
  const { can } = useAuthorization();
  const status = tour.status;
  const id = tour.id;

  const isAllowed = React.useMemo(() => {
    switch (status) {
      case TourStatusEnum.Closed:
        return true;
      default:
        return false;
    }
  }, [status]);

  const unclosePermission =
    can(AuthorizationRules.ToursRevertClose) && isAllowed;

  const onUnclose = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('common:do_you_want_to_unclose_this_tour'),
      title: t('common:confirmation'),
      onPositiveAction: async (onClose) => {
        try {
          const result = await uncloseMutate.mutateAsync({
            tourId: tour.refId!,
          });
          onDismiss(id);
          notification.success({ message: result?.message });
          onClose();

          onRequestApproval(id);
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (onClose) => {
        onClose();
      },
    });
  }, [dialog, id, onDismiss, onRequestApproval, t, tour.refId, uncloseMutate]);

  const uncloseAction: PopoverMenuType = {
    key: `unclose-${id}`,
    label: t('common:unclose_tour'),
    onClick: onUnclose,
    isAllowed: unclosePermission,
    isError: true,
  };

  const uncloseComponent = unclosePermission && (
    <DeleteButton
      text={t('common:unclose_tour')}
      onClick={onUnclose}
      isLoading={uncloseMutate.isLoading}
      icon={<CloseSVG color={stylesColors.redDefault} width={16} height={16} />}
    />
  );

  return {
    uncloseMutate,
    onUnclose,
    unclosePermission,
    uncloseComponent,
    uncloseAction,
  };
}
